import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import styles from './Scenario.module.css';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import ViewScenario from './ViewScenario';
import ViewScenarioMobile from './ViewScenarioMobile';
import EditScenario from './EditScenario';
import ResponsiveLayout from '../navigation/ResponsiveLayout';

type RouteParams = {
  scenarioId: string;
};

function Scenario(): JSX.Element | null {
  const { scenarioId } = useParams() as RouteParams;
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('edit') === 'true';
  const fromChats = searchParams.get('fromChats') === 'true';
  
  const [definition, setDefinition] = useState<ScenarioDefinitionType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [lastEditMode, setLastEditMode] = useState<boolean>(isEditMode);
  const [lastLocation, setLastLocation] = useState<string>(location.search);

  // Memoize the loadDefinition function to use in dependencies
  const loadDefinition = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const scenarioDefinition = await api.scenarioDefinitions.get(scenarioId as string);
      setDefinition(scenarioDefinition);
      setError(null);
    } catch (err) {
      console.error('Failed to load scenario definition:', err);
      setError('Failed to load scenario definition');
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  // Initial load
  useEffect(() => {
    if (!scenarioId) return;
    loadDefinition();
  }, [scenarioId, loadDefinition]);

  // Detect when we switch from edit mode to view mode and refresh the data
  useEffect(() => {
    if (lastEditMode && !isEditMode) {
      // We just switched from edit mode to view mode, refresh the data
      loadDefinition();
    }
    setLastEditMode(isEditMode);
  }, [isEditMode, lastEditMode, loadDefinition]);

  // Detect URL changes (e.g., when navigating back from edit mode)
  useEffect(() => {
    if (location.search !== lastLocation) {
      setLastLocation(location.search);
      if (!isEditMode) {
        // If we're in view mode after a URL change, refresh the data
        loadDefinition();
      }
    }
  }, [location.search, lastLocation, isEditMode, loadDefinition]);

  // If coming from Chats, create a session and redirect directly
  useEffect(() => {
    if (fromChats && definition && user?.default_persona?.id) {
      createSessionAndRedirect();
    }
  }, [fromChats, definition, user]);

  const createSessionAndRedirect = async (): Promise<void> => {
    try {
      const personaId = user?.default_persona?.id;
      
      if (!personaId) {
        console.error('No default persona available');
        return;
      }
      
      // Create a new session using the scenarios API
      const session = await api.scenarios.create(
        scenarioId,
        personaId
      );
      
      // Navigate to the session page
      navigate(`/sessions/${session.id}`, { replace: true });
    } catch (error) {
      console.error('Failed to create session:', error);
      // Error is handled in the UI below, no need to redirect
    }
  };

  useEffect(() => {
    // Handle window resize to determine if mobile view should be shown
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleResize();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Function to handle definition updates from child components
  const handleDefinitionUpdate = useCallback((updatedDefinition: ScenarioDefinitionType) => {
    setDefinition(updatedDefinition);
  }, []);

  if (loading) {
    return (
      <ResponsiveLayout>
        <div className={styles.loading}>Loading...</div>
      </ResponsiveLayout>
    );
  }

  if (error) {
    return (
      <ResponsiveLayout>
        <div className={styles.error}>{error}</div>
      </ResponsiveLayout>
    );
  }

  if (!definition) {
    return null;
  }

  const isEditable = user?.username === definition.author;

  return (
    isEditMode ? (
      isEditable ? (
        <EditScenario 
          definition={definition} 
          onDefinitionUpdate={handleDefinitionUpdate}
        />
      ) : (
        // Redirect non-authors trying to edit
        navigate(`/scenarios/${scenarioId}`),
        null
      )
    ) : (
      <ResponsiveLayout>
        {isMobile ? (
          <ViewScenarioMobile 
            definition={definition} 
            isEditable={isEditable} 
            onRefresh={loadDefinition}
          />
        ) : (
          <ViewScenario 
            definition={definition} 
            isEditable={isEditable} 
            onRefresh={loadDefinition}
          />
        )}
      </ResponsiveLayout>
    )
  );
}

export default Scenario; 